import type { ParcelFilters, ParcelSort } from "@designboticteam/map";
import type { ParcelsListQueryMessage, ParcelsListResponse, ParcelsListResult, ViewportBounds } from "./types";

type ParcelsListQuery = {
	bounds: ViewportBounds;
	zoom: number;
	filters: ParcelFilters;
	sort: ParcelSort;
	favouriteIds: Array<string>;
};

type ParcelsListClientParams = {
	url: string;
	sourceLayer: string;
	onResult: (result: ParcelsListResult) => void;
	onError: (message: string) => void;
};

/** */
class ParcelsListClient {
	private readonly worker: Worker;
	private readonly onResult: (result: ParcelsListResult) => void;
	private readonly onError: (message: string) => void;

	private nextRequestId = 1;
	private inFlightRequestId: number | null = null;
	private queuedQuery: ParcelsListQuery | null = null;

	constructor({ url, sourceLayer, onResult, onError }: ParcelsListClientParams) {
		this.onResult = onResult;
		this.onError = onError;

		this.worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
		this.worker.addEventListener("message", this.handleMessage);

		// The worker's base URL is its own script, not the document, so the archive path is resolved here.
		this.worker.postMessage({ kind: "config", url: new URL(url, window.location.href).href, sourceLayer });
	}

	public request(query: ParcelsListQuery): void {
		if (this.inFlightRequestId !== null) {
			this.queuedQuery = query;
			return;
		}

		this.send(query);
	}

	public dispose(): void {
		this.worker.removeEventListener("message", this.handleMessage);
		this.worker.terminate();
	}

	private send(query: ParcelsListQuery): void {
		const requestId = this.nextRequestId++;
		this.inFlightRequestId = requestId;

		const message: ParcelsListQueryMessage = { kind: "query", requestId, ...query };
		this.worker.postMessage(message);
	}

	private readonly handleMessage = (event: MessageEvent<ParcelsListResponse>): void => {
		const response = event.data;

		if (response.requestId !== this.inFlightRequestId) {
			return;
		}

		this.inFlightRequestId = null;

		// Taken before the callback runs: publishing a result can end up asking for another one, and that
		// query must not be sent twice.
		const queued = this.queuedQuery;
		this.queuedQuery = null;

		if (response.kind === "error") {
			this.onError(response.message);
		} else {
			this.onResult(response);
		}

		if (queued !== null && this.inFlightRequestId === null) {
			this.send(queued);
		}
	};
}

export { ParcelsListClient, type ParcelsListQuery };
